import { OptionalTwilioNumber, RegisteredTwilioNumber } from "interfaces/db"
import { MoreHorizontal, Phone, Search, Loader, CheckCircle, AlertTriangle, GripVertical } from 'lucide-react';
import { useEffect, useState } from "react";
import { NumberType, CountryOptions, defaultCountryOptions, format_number } from "./cfg";
import { getServicesManager } from "services";

export type NumberSearchProps = {
    registeredNumbers: RegisteredTwilioNumber[]
    handlePurchase: (number: OptionalTwilioNumber) => void
    purchasingNumbers: string[]
    disabled: boolean
}

const COUNTRY_NAME_TO_OPTIONS: Record<string, CountryOptions> = {
    // Default UK to Mobile unless team is only allowed to rent Local numbers;
    // all other countries default to Local (see getTwilioRegistrations call below).
    'United Kingdom': {code: '+44', name: 'United Kingdom', defaultNumberType: 'Mobile'},
    'Germany': {code: '+49', name: 'Germany'},
    'Mexico': {code: '+52', name: 'Mexico'},
}

const COUNTRY_NAME_TO_CODE: Record<string, string> = {
    'United States': 'US',
    'Canada': 'CA',
    'United Kingdom': 'GB',
    'Germany': 'DE',
    'Mexico': 'MX',
}

export function NumberSearch({ registeredNumbers, handlePurchase, disabled, purchasingNumbers }: NumberSearchProps) {
    const [countryOptions, setCountryOptions] = useState<CountryOptions[]>(defaultCountryOptions)
    const [availableNumbers, setAvailableNumbers] = useState<OptionalTwilioNumber[]>([])
    const [searchParams, setSearchParams] = useState({
      country: 'United States',
      query: '',
      numberType: 'Local' as NumberType,
    })

    useEffect(() => {
      getServicesManager().getTwilioRegistrations().then(regs => {
        if (regs === null) { return }
        const additionalOptions: Record<string, CountryOptions> = {}
        for (const reg of regs) {
          if (reg.purpose !== 'NUMBER_RENTAL') { continue }
          const rentalTypeMatch = reg.number_rental_type.match(/([^:]+): (\S+) - (.+)$/)
          if (!rentalTypeMatch) { continue }  // probably an error
          const [countryName, numberType, ownerType] = rentalTypeMatch.slice(1)
          const countryOption = COUNTRY_NAME_TO_OPTIONS[countryName]
          if (!countryOption) { continue }  // probably an error
          const defaultNumberType = countryOption.defaultNumberType ?? 'Local'
          if (numberType === defaultNumberType || !additionalOptions[countryName]) {
            additionalOptions[countryName] = {...countryOption, defaultNumberType: numberType as NumberType}
          }
        }
        setCountryOptions([...defaultCountryOptions, ...Object.values(additionalOptions)])
      })
    }, [])

    const getQueryParams = () => {
      const params: { [key: string]: string | null } = {}
      params.number_type = searchParams.numberType
      params.iso_country_code = COUNTRY_NAME_TO_CODE[searchParams.country] ?? 'US'  // Default to US instead of undefined
      if (searchParams.query.match(/^\d+$/)) {
        params.area_code = searchParams.query
      } else if (['US', 'CA'].includes(params.iso_country_code) && searchParams.query.match(/^[A-Za-z]{2}$/)) {
        params.state_or_province = searchParams.query
      } else {
        params.locality = searchParams.query
      }
      return params
    }

    useEffect(() => {
        const params = getQueryParams()
        getServicesManager().getOptionsForTwilioNumber(params).then((numbers) => {
            if (!numbers) return
            setAvailableNumbers(numbers)
        })
    }, [searchParams.numberType, searchParams.country, searchParams.query])

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        const query = getQueryParams()
        getServicesManager().getOptionsForTwilioNumber(query).then((numbers) => {
          if (!numbers) return
          setAvailableNumbers(numbers)
        })
    }

    return (
    <div className="w-full flex flex-col gap-2">
    <div className="shadow-sm bg-white border border-gray-200 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-700 flex items-center">
        <Search className="mr-2" size={20} />
        Search Numbers
        </h2>
        <form onSubmit={handleSearch} className="flex items-center space-x-4">
        <select
            value={searchParams.country}
            onChange={(e) => setSearchParams({
              ...searchParams,
              country: e.target.value,
              numberType: countryOptions.find(c => c.name === e.target.value)?.defaultNumberType ?? 'Local',
            })}
            className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
            {countryOptions.map((country) => (
            <option key={country.name} value={country.name}>
                {country.name} ({country.code})
            </option>
            ))}
        </select>
        <input
            type="text"
            value={searchParams.query}
            onChange={(e) => setSearchParams({...searchParams, query: e.target.value})}
            placeholder="Enter area code, state (2 letter code), or city"
            className="flex-grow p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button type="submit" className="bg-blue-500 text-white p-2 rounded-md hover:bg-blue-600 transition duration-200 flex items-center justify-center">
            <Search size={20} />
        </button>
        </form>

    <div className="overflow-x-auto mt-2">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50">
            <th className="p-2 text-left font-semibold">Number</th>
            <th className="p-2 text-left font-semibold">City</th>
            <th className="p-2 text-left font-semibold">State</th>
            <th className="p-2 text-left font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {availableNumbers.map((v) => {
            const { friendly_name, locality, region, phone_number } = v
            // Skip if the number is already registered
            if (registeredNumbers.find((r) => r.number && phone_number &&
                format_number(r.number) === format_number(phone_number))) return null
            
            return (<tr key={friendly_name} className="border-b hover:bg-gray-50">
              <td className="p-2">{friendly_name}</td>
              <td className="p-2">{locality}</td>
              <td className="p-2">{region}</td>
              <td className="p-2">
                <button
                  onClick={() => handlePurchase(v)}
                  disabled={purchasingNumbers.includes(phone_number) || disabled}
                  className={`bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 transition duration-200 flex items-center ${
                    (purchasingNumbers.includes(phone_number) || disabled) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {purchasingNumbers.includes(phone_number) ? (
                    <>
                      <Loader className="mr-1 animate-spin" size={16} />
                      Purchasing...
                    </>
                  ) : (
                    <>
                      <Phone className="mr-1" size={16} />
                      Purchase
                    </>
                  )}
                </button>
              </td>
            </tr>)
          })}
        </tbody>
      </table>
    </div>
  </div>
  </div>)
}